import { Card, CardContent } from '@design-system/Card';
import { List, ListItem } from '@design-system/List';
import { Link } from '@design-system/Link';
import type { SubscriptionStatus } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import {
  isSubscriptionCancelled,
  isSubscriptionActiveState,
  isSubscriptionPaused,
} from '../utils/smpHelpers';
import { useSMPNavigate } from '../hooks/useSMPNavigate';
import {
  SMP_PAUSE_SUBSCRIPTION_PATH,
  SMP_CANCEL_SUBSCRIPTION_PATH,
  SMP_REACTIVATE_PATH,
  SMP_RESUME_SUBSCRIPTION_PATH,
} from '@/constants/paths';

interface SMPSubscriptionDetailQuickActionsProps {
  subscriptionStatus: SubscriptionStatus;
}

// Componentized subscriptions drop Add/Remove Products and Skip Next Order —
// only Pause/Resume + Cancel (+ Reactivate) carry over, per
// SMP_COMPONENTIZATION_TECH_SPEC_v1.md §5.0.
export const SMPSubscriptionDetailQuickActions = ({
  subscriptionStatus,
}: SMPSubscriptionDetailQuickActionsProps) => {
  const { withSubId } = useSMPNavigate();
  const isCancelled = isSubscriptionCancelled(subscriptionStatus);
  const isActiveState = isSubscriptionActiveState(subscriptionStatus);
  const isPaused = isSubscriptionPaused(subscriptionStatus);

  return (
    <div className="flex flex-col gap-6" data-testid="smp-detail-quickActions">
      <h2
        className="typography-h2 text-primary px-1"
        data-testid="smp-detail-quickActionsTitle"
      >
        Quick Actions
      </h2>

      <Card data-testid="smp-detail-quickActionsCard">
        <CardContent className="p-0" data-testid="smp-detail-quickActionsContent">
          <List>
            {isActiveState && (
              <>
                {isPaused ? (
                  <ListItem asChild data-testid="smp-detail-resume">
                    <Link to={withSubId(SMP_RESUME_SUBSCRIPTION_PATH)}>
                      Resume Subscription
                    </Link>
                  </ListItem>
                ) : (
                  <ListItem asChild data-testid="smp-detail-pause">
                    <Link to={withSubId(SMP_PAUSE_SUBSCRIPTION_PATH)}>
                      Pause Subscription
                    </Link>
                  </ListItem>
                )}

                <ListItem asChild data-testid="smp-detail-cancel">
                  <Link to={withSubId(SMP_CANCEL_SUBSCRIPTION_PATH)}>
                    Cancel Subscription
                  </Link>
                </ListItem>
              </>
            )}

            {isCancelled && (
              <ListItem asChild data-testid="smp-detail-reactivate">
                <Link to={withSubId(SMP_REACTIVATE_PATH)}>
                  Reactivate Subscription
                </Link>
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>
    </div>
  );
};
